import Emitter from "es6-event-emitter";
import { generateSecurePreviewURL, generateSecureImageURL } from "./utils";

const CREDENTIALS_TIMEOUT = 1000 * 3;
const NUMBER_OF_RETRIES = 2;
const DOWNLOAD_CREDENTIALS_EVENTS = {
  SUCCESS: "download_credentails:success",
  ERROR: "download_credentails:error",
};

export default class CDNAssetsDownloader extends Emitter {
  credentials = null;
  _requestPreviewDownloadCounter = 0;
  _requestImgDownloadCounter = 0;
  _requestDownloadLimit = 1000;
  _downloadCredentialsTimeLimit = 60 * 5;
  _downloadCredentialsLimit = 3;
  _eventEmitter = new Emitter();
  constructor(apiManager, appState, sessionManager) {
    super();
    // you don’t need the whole appState,
    // if this class is kept in the appState it is better that you just pass the userId, sessionId and downloadId
    // if one of these 3 changes you have to pass it again to make sure you have the latest version (and do a new getDownloadCredentials call)
    this.apiManager = apiManager;
    this._appState = appState;
    this._sessionManager = sessionManager;
  }

  _getPreviewUrl(size, pageId) {
    if (this.credentials && this.credentials.PreviewPattern) {
      let a = this.credentials.PreviewPattern.split("%PUBPAGE_ID%").join(
        `${pageId}`
      );
      a = a.split("%SIZENAME%").join(`${size}`);
      return a;
    }
  }

  getPdfUrl(pageId) {
    if (this.credentials && this.credentials.PdfPattern) {
      return this.credentials.PdfPattern.split("%PUBPAGE_ID%").join(
        `${pageId}`
      );
    }
  }

  getJsonUrl(name, pubId) {
    if (this.credentials && this.credentials.JsonPattern) {
      return this.credentials.JsonPattern.split("%JSON_NAME%").join(
        `${name}-${pubId}`
      );
    }
  }

  _getImageUrl(size, imageId) {
    if (this.credentials && this.credentials.ImagePattern) {
      let a = this.credentials.ImagePattern.split("%IMAGE_ID%").join(
        `${imageId}`
      );
      a = a.split("%SIZENAME%").join(`${size}`);
      return a;
    }
  }

  getTDMPattern() {
    if (this.credentials && this.credentials.PublicationTdmJsonPattern) {
      return this.credentials.PublicationTdmJsonPattern;
    }
  }

  /**
   * Request credentials
   * We will only generate new credentials when not in fallback mode and when no request is already ongoing
   */
  requestDownloadCredentials(force = false) {
    if (this._appState.newDownloaderFallbackMode) {
      return Promise.resolve();
    }
    if (!this._appState.isReaderSessionValidForState()) {
      this._requestDownloadPromiseInProgress = false;
    }
    if (!this._requestDownloadPromiseInProgress || force) {
      this._requestDownloadPromiseInProgress = true;
      return new Promise((resolve, reject) => {
        this._getDownloadCredentials()
          .then(() => {
            this._eventEmitter.trigger(DOWNLOAD_CREDENTIALS_EVENTS.SUCCESS);
            setTimeout(() => {
              this._requestDownloadPromiseInProgress = false;
              this._eventEmitter.trigger(DOWNLOAD_CREDENTIALS_EVENTS.SUCCESS);
            }, CREDENTIALS_TIMEOUT);
            resolve("resolved");
          })
          .catch((error) => {
            this._requestDownloadPromiseInProgress = false;
            this._eventEmitter.trigger(DOWNLOAD_CREDENTIALS_EVENTS.ERROR);
            reject(error);
          });
      });
    } else {
      return new Promise((resolve, reject) => {
        this._eventEmitter.on(DOWNLOAD_CREDENTIALS_EVENTS.SUCCESS, resolve);
        this._eventEmitter.on(DOWNLOAD_CREDENTIALS_EVENTS.ERROR, reject);
      });
    }
  }

  /**
   * requests credentials to the api if there is a valid downloadId available
   */
  _getDownloadCredentials() {
    const downloadId = this._appState.downloadId;
    if (downloadId) {
      return this.apiManager
        .getDownloadCredentials(
          this._appState.userId,
          this._appState.sessionId,
          downloadId
        )
        .then((resp) => {
          if (resp && resp.status === 200) {
            return resp.json().then((result) => {
              this.credentials = result;
              return this.credentials;
            });
          } else {
            return Promise.reject();
          }
        });
    } else {
      // the downloadId is 0 when reading using social sharing
      this._appState.newDownloaderFallbackMode = true;
      return Promise.resolve();
    }
  }

  /**
   * Loads a preview image as background on the given element
   */
  loadBackgroundPreviewOnElement(sizes, publicationPageId, element) {
    if (element) {
      return this.loadPreview(sizes, publicationPageId, true).then((src) => {
        element.style.backgroundImage = "url(" + src + ")";
        const imgAttribute = element.getAttribute("data-img");
        if (imgAttribute === "") {
          element.setAttribute("data-img", src);
        }
      });
    }
    return Promise.reject();
  }

  /**
   * Loads a preview image
   * Returns either the image html element where the preview is loaded or the url that loads the image
   */
  loadPreview(sizes, publicationPageId, returnUrl = false, numberOfTries = 1) {
    const imageSrc = this._appState.newDownloaderFallbackMode
      ? generateSecurePreviewURL(
          sizes.nonNamedSize,
          publicationPageId,
          this._sessionManager.session.SessionInfo.SessionId,
          this._sessionManager.userId,
          this._appState.downloadId
        )
      : this._getPreviewUrl(sizes.namedSize, publicationPageId);
    return this._loadUrlOnImageElement(
      imageSrc,
      numberOfTries <= NUMBER_OF_RETRIES &&
        !this._appState.newDownloaderFallbackMode
    )
      .then((image) => {
        return returnUrl ? imageSrc : image;
      })
      .catch((error) => {
        if (error.status && error.status === 403) {
          if (numberOfTries >= NUMBER_OF_RETRIES) {
            this._appState.newDownloaderFallbackMode = true;
            return this.loadPreview(
              sizes,
              publicationPageId,
              returnUrl,
              numberOfTries + 1
            );
          } else {
            return this.requestDownloadCredentials().then(() => {
              return this.loadPreview(
                sizes,
                publicationPageId,
                returnUrl,
                numberOfTries + 1
              );
            });
          }
        }
        return Promise.reject();
      });
  }

  /**
   * Loads an image onto an element
   * This can be as an image or as a background image
   * can return the ratio if needed
   */
  loadImageOnElement(sizes, contentItemId, element, getRatio, backgroundImage) {
    if (element) {
      return this._loadImage(
        sizes,
        contentItemId,
        backgroundImage ? undefined : element
      ).then((src) => {
        if (backgroundImage) {
          element.style.backgroundImage = "url(" + src + ")";
        }
        if (getRatio) {
          return new Promise((resolve) => {
            const image = new Image();
            image.onload = function () {
              resolve(image.height / image.width);
            };
            image.src = src;
          });
        }
        return Promise.resolve();
      });
    }
    return Promise.reject();
  }

  /**
   * Loads an image onto a certain element if provided
   */
  _loadImage(sizes, contentItemId, element, numberOfTries = 1) {
    const imageSrc = this._appState.newDownloaderFallbackMode
      ? generateSecureImageURL(
          sizes.nonNamedSize,
          contentItemId,
          this._sessionManager.session.SessionInfo.SessionId,
          this._sessionManager.userId,
          this._appState.downloadId
        )
      : this._getImageUrl(sizes.namedSize, contentItemId);
    return this._loadUrlOnImageElement(
      imageSrc,
      numberOfTries <= NUMBER_OF_RETRIES &&
        !this._appState.newDownloaderFallbackMode,
      element
    )
      .then(() => {
        return imageSrc;
      })
      .catch((error) => {
        if (error.status && error.status === 403) {
          if (numberOfTries >= NUMBER_OF_RETRIES) {
            this._appState.newDownloaderFallbackMode = true;
            return this._loadImage(
              sizes,
              contentItemId,
              element,
              numberOfTries + 1
            );
          } else {
            return this.requestDownloadCredentials().then(() => {
              return this._loadImage(
                sizes,
                contentItemId,
                element,
                numberOfTries + 1
              );
            });
          }
        }
        return Promise.reject();
      });
  }

  /**
   * Loads a url on an image element if passed, else it will generate a new one
   * If getStatusCode is true then it will return the status code if a failure occurs
   * @param {*} url
   * @param {*} getStatusCode
   */
  _loadUrlOnImageElement(url, getStatusCode, image = undefined) {
    return new Promise((resolve, reject) => {
      const img = image ? $(image) : $("<img />");
      // remove previous event listeners in case of continues retries
      img.off("load");
      img.off("error");

      // get CORS headers in response
      img.crossOrigin = "Anonymous";

      img.on("load", () => {
        resolve(img);
      });
      img.on("error", (err) => {
        if (getStatusCode) {
          fetch(url).then((resp) => {
            if (resp.status === 200) {
              resolve(img);
            } else {
              reject(resp);
            }
          });
        } else {
          reject(err);
        }
      });
      img.attr("src", url);
    });
  }
}



// WEBPACK FOOTER //
// ./src/common/utilities/cdnAssetsDownloader.js